/**
 * @fileoverview Hook for handling edit conflicts with user-friendly resolution.
 *
 * This hook provides state and handlers for when two users try to edit
 * the same resource and a conflict is detected.
 *
 * @module hooks/useConflictResolution
 */

import { useState, useCallback } from 'react';

/**
 * Hook for managing conflict resolution state.
 *
 * @returns {Object} Conflict resolution state and handlers
 *
 * @example
 * const {
 *   conflict,
 *   isResolving,
 *   handleConflictError,
 *   resolveWithMyChanges,
 *   resolveWithServerChanges,
 *   dismissConflict
 * } = useConflictResolution();
 *
 * // When API returns 409 conflict
 * try {
 *   await updateTransaction(data);
 * } catch (error) {
 *   if (handleConflictError(error, data)) {
 *     // Conflict modal will be shown
 *     return;
 *   }
 *   // Handle other errors
 * }
 */
export function useConflictResolution() {
    const [conflict, setConflict] = useState(null);
    const [isResolving, setIsResolving] = useState(false);

    /**
     * Check if an error is a conflict error and set up the conflict state.
     *
     * @param {Object} error - Error from API call (ConflictError instance or plain object)
     * @param {Object} originalData - The data the user tried to save
     * @returns {boolean} True if this was a conflict error
     */
    const handleConflictError = useCallback((error, originalData) => {
        // Check if this is a conflict response
        // ConflictError class uses isConflict=true, plain objects might have error='conflict'
        if (error?.isConflict || error?.error === 'conflict' || error?.status === 409) {
            // modifiedBy can be at top level or in currentData.lastModifiedBy (from backend)
            const modifiedBy = error.modifiedBy || error.currentData?.lastModifiedBy;
            const modifiedAt = error.modifiedAt || error.currentData?.updatedAt;

            setConflict({
                yourData: originalData,
                serverData: error.currentData,
                yourVersion: error.yourVersion,
                currentVersion: error.currentVersion,
                message: error.message || 'This resource was modified by another user',
                modifiedBy,
                modifiedAt,
            });
            return true;
        }
        return false;
    }, []);

    /**
     * Resolve the conflict by keeping the user's changes.
     * The update function should be called with the current server version.
     *
     * @param {Function} updateFunction - Function to call with merged data
     * @returns {Promise<Object>} Result of the update
     */
    const resolveWithMyChanges = useCallback(async (updateFunction) => {
        if (!conflict) return null;

        setIsResolving(true);
        try {
            // Retry with the current server version
            const result = await updateFunction({
                ...conflict.yourData,
                version: conflict.currentVersion,
            });
            setConflict(null);
            return result;
        } catch (error) {
            // If another conflict, update the conflict state
            if (error?.isConflict || error?.error === 'conflict') {
                setConflict(prev => ({
                    ...prev,
                    serverData: error.currentData,
                    currentVersion: error.currentVersion,
                }));
            }
            throw error;
        } finally {
            setIsResolving(false);
        }
    }, [conflict]);

    /**
     * Resolve the conflict by accepting the server's changes.
     * Returns the server data for the component to use.
     *
     * @returns {Object} Server's current data
     */
    const resolveWithServerChanges = useCallback(() => {
        if (!conflict) return null;

        const serverData = conflict.serverData;
        setConflict(null);
        return serverData;
    }, [conflict]);

    /**
     * Dismiss the conflict without resolving (cancel the edit).
     */
    const dismissConflict = useCallback(() => {
        setConflict(null);
    }, []);

    /**
     * Check if there's an active conflict.
     */
    const hasConflict = conflict !== null;

    return {
        conflict,
        hasConflict,
        isResolving,
        handleConflictError,
        resolveWithMyChanges,
        resolveWithServerChanges,
        dismissConflict,
    };
}

export default useConflictResolution;
